import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  User,
  sendEmailVerification
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { Analytics, ErrorTracker } from '../utils/analytics';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    Analytics.loginAttempt(email);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      Analytics.loginSuccess(email);
    } catch (error: any) {
      Analytics.loginError(error.code, email);
      ErrorTracker.trackError(error, 'login');
      throw error;
    }
  };

  const register = async (email: string, password: string): Promise<void> => {
    Analytics.registerAttempt(email);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Send email verification
      await sendEmailVerification(userCredential.user);
      
      // Sign out the user after registration
      await signOut(auth);
      
      Analytics.registerSuccess(email);
    } catch (error: any) {
      Analytics.registerError(error.code, email);
      ErrorTracker.trackError(error, 'register');
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
      Analytics.logout();
    } catch (error: any) {
      ErrorTracker.trackError(error, 'logout');
      throw error;
    }
  };

  const sendVerificationEmail = async (): Promise<void> => {
    if (!user) {
      throw new Error('No user logged in');
    }
    
    try {
      await sendEmailVerification(user);
      Analytics.verificationEmailSent(user.email || '');
    } catch (error: any) {
      ErrorTracker.trackError(error, 'sendVerificationEmail');
      throw error;
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isEmailVerified: user?.emailVerified || false,
    login,
    register,
    logout,
    sendVerificationEmail,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
